import { useState } from "react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { SpotImage } from "@/common/model/spotimages/SpotImage";
import ImageLightbox from "./ImageLightbox";
import ConfirmDialog from "@/components/common/ConfirmDialog";

interface AvailableImagesSectionProps {
  images: SpotImage[];
  onAddToSelection: (filename: string) => Promise<void>;
  onDelete: (filename: string) => Promise<void>;
}

const AvailableImagesSection: React.FC<AvailableImagesSectionProps> = ({
  images,
  onAddToSelection,
  onDelete,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addingImage, setAddingImage] = useState<string | null>(null);
  const [lightboxImage, setLightboxImage] = useState<SpotImage | null>(null);

  if (images.length === 0) {
    return null;
  }

  const handleAddToSelection = async (filename: string) => {
    setAddingImage(filename);
    try {
      await onAddToSelection(filename);
      // Close lightbox after adding
      if (lightboxImage?.filename === filename) {
        setLightboxImage(null);
      }
    } finally {
      setAddingImage(null);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;

    setIsDeleting(true);
    try {
      await onDelete(pendingDelete);
      // Close lightbox if the deleted image was being viewed
      if (lightboxImage?.filename === pendingDelete) {
        setLightboxImage(null);
      }
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
    }
  };

  const handleLightboxAdd = async (filename: string) => {
    await handleAddToSelection(filename);
  };

  const handleLightboxDelete = (filename: string) => {
    setPendingDelete(filename);
  };

  // Count images with and without scores
  const withScore = images.filter((img) => img.score !== null).length;
  const withoutScore = images.length - withScore;

  return (
    <div className="mt-8 border-t pt-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium"
      >
        {isExpanded ? (
          <ChevronDownIcon className="h-5 w-5" />
        ) : (
          <ChevronRightIcon className="h-5 w-5" />
        )}
        <span>Available Images ({images.length})</span>
        <span className="text-sm text-gray-500 font-normal">
          {withScore > 0 && `${withScore} with ML score`}
          {withScore > 0 && withoutScore > 0 && ", "}
          {withoutScore > 0 && `${withoutScore} unscored`}
        </span>
      </button>

      {isExpanded && (
        <div className="mt-4">
          <p className="text-sm text-gray-500 mb-4">
            Click an image to preview, or use the + button to add it to your selection.
            Images are sorted by ML score (highest first), unscored images appear at the end.
          </p>

          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
            {images.map((image) => (
              <div
                key={image.filename}
                className="relative group bg-gray-100 rounded-lg overflow-hidden cursor-pointer"
                onClick={() => setLightboxImage(image)}
              >
                {/* Image */}
                <div className="aspect-video">
                  <img
                    src={image.publicUrl}
                    alt={image.filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                {/* Score badge */}
                <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 text-white text-xs rounded">
                  {image.score !== null ? (
                    `${(image.score * 100).toFixed(0)}%`
                  ) : (
                    <span className="text-gray-300 text-[10px]">No score</span>
                  )}
                </div>

                {/* Action buttons overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToSelection(image.filename);
                    }}
                    disabled={addingImage === image.filename}
                    className="p-1.5 bg-green-500 text-white rounded-full shadow hover:bg-green-600 transition-colors disabled:opacity-50"
                    title="Add to selection"
                  >
                    {addingImage === image.filename ? (
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <PlusIcon className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingDelete(image.filename);
                    }}
                    className="p-1.5 bg-red-500 text-white rounded-full shadow hover:bg-red-600 transition-colors"
                    title="Delete permanently"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox */}
      <ImageLightbox
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
        image={lightboxImage}
        images={images}
        isCover={false}
        onAddToSelection={handleLightboxAdd}
        onDelete={handleLightboxDelete}
        onNavigate={setLightboxImage}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title="Delete Image Permanently"
        message="Are you sure you want to permanently delete this image? This will remove the file from storage and cannot be undone."
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        type="danger"
      />
    </div>
  );
};

export default AvailableImagesSection;
